import { useCallback, useState } from "react";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import { useTranslation } from "react-i18next";
import useSidebar from "../../../../SidebarProvider/useSidebar";
import useHyroxTier from "hooks/useHyroxTier";
import useCanDo from "hooks/useCanDo";
import CheckoutService from "services/CheckoutService";
import TooltipHelper from "components/TooltipHelper";
import Typography from "components/Typography";
import UpgradeIcon from "components/Icons/UpgradeIcon";
import StarIcon from "components/Icons/StarIcon";
import ListItemCategory from "../ListItemCategory";
import { SecondaryItem, StyledLink } from "../../..";
import CircularProgress from "@mui/material/CircularProgress";
import { styled } from "@mui/material";
import { hyroxEliteDemoUrl } from "config/knowledgebase-links";

const UpgradeButton = styled("button")({
  all: "unset",
  padding: "4px 8px",
  gap: "14px",
  display: "flex",
  alignItems: "center",
  minHeight: "28px",
  borderRadius: "4px",
  cursor: "pointer",
  color: "white",
  textDecoration: "none",
  width: "100%",
  boxSizing: "border-box",

  "&:hover": {
    backgroundColor: "rgba(255, 255, 255, 0.1)",
  },
});

function HyroxUpgradeSection() {
  const { t } = useTranslation();
  const { open } = useSidebar();
  const { isHyrox, isHyroxT0, isHyroxT2 } = useHyroxTier();
  const canDo = useCanDo();
  const [isLoading, setIsLoading] = useState(false);

  const showUpgradeButton = isHyroxT0 && canDo("subscription.any");
  const showEliteDemoLink = isHyrox && !isHyroxT2;

  const handleUpgradeClick = useCallback(async () => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      const response = await CheckoutService.createAdvanceCheckout();
      window.location.href = response.checkoutUrl;
    } catch {
      setIsLoading(false);
    }
  }, [isLoading]);

  if (!showUpgradeButton && !showEliteDemoLink) return null;

  return (
    <List
      data-sidebar="group"
      sx={{
        display: "flex",
        flexDirection: "column",
        padding: "0 8px",
        gap: "4px",
      }}
    >
      <ListItemCategory>{t("sidebar.upgrade_label")}</ListItemCategory>
      {showUpgradeButton && (
        <TooltipHelper placement="right" title={open ? null : t("sidebar.upgrade_to_advance")}>
          <ListItem disablePadding>
            <UpgradeButton onClick={handleUpgradeClick} aria-label={t("sidebar.upgrade_to_advance")}>
              {isLoading ? (
                <CircularProgress size={20} sx={{ color: "white" }} />
              ) : (
                <UpgradeIcon sx={{ color: "#FFF", fontSize: "20px" }} />
              )}
              {open ? (
                <Typography variant="TextS" noWrap>
                  {t("sidebar.upgrade_to_advance")}
                </Typography>
              ) : null}
            </UpgradeButton>
          </ListItem>
        </TooltipHelper>
      )}
      {showEliteDemoLink && (
        <TooltipHelper placement="right" title={open ? null : t("sidebar.book_elite_demo")}>
          <ListItem disablePadding>
            <StyledLink to={hyroxEliteDemoUrl} target="_blank" rel="noopener noreferrer" sx={{ width: "100%" }}>
              <SecondaryItem sx={{ color: "white" }}>
                <StarIcon sx={{ color: "#FFF", fontSize: "20px" }} />
                {open ? (
                  <Typography
                    variant="TextS"
                    sx={{ whiteSpace: "normal", lineHeight: "19.6px", display: "flex", flexDirection: "column" }}
                  >
                    <span>{t("sidebar.book_elite_demo")}</span>
                    <span>{t("sidebar.book_elite_demo_subtitle")}</span>
                  </Typography>
                ) : null}
              </SecondaryItem>
            </StyledLink>
          </ListItem>
        </TooltipHelper>
      )}
    </List>
  );
}

export default HyroxUpgradeSection;
